import { useState, useRef, useEffect } from 'react';
import type { AppLockInstance } from '@ascend/applock';

interface LockMenuProps {
  lock: AppLockInstance;
}

export function LockMenu({ lock }: LockMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) return;
    try {
      await lock.changePassword(currentPw, newPw);
      setChangingPassword(false);
      setCurrentPw('');
      setNewPw('');
    } catch {
      alert('Failed to change password. Check your current password.');
    }
  };

  const handleRemovePassword = async () => {
    const pw = prompt('Enter your current password to remove app lock:');
    if (!pw) return;
    try {
      await lock.removePassword(pw);
    } catch {
      alert('Failed to remove password. Check your current password.');
    }
  };

  if (changingPassword) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
        <input type="password" placeholder="Current" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} style={{ width: 80, padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }} />
        <input type="password" placeholder="New" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={{ width: 80, padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }} />
        <button className="btn btn-sm" onClick={handleChangePassword}>Save</button>
        <button className="btn btn-sm" onClick={() => { setChangingPassword(false); setCurrentPw(''); setNewPw(''); }}>Cancel</button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-sm"
        onClick={() => setOpen(!open)}
        aria-label="Lock options"
        title="Lock options"
      >
        Lock
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          right: 0,
          top: '100%',
          marginTop: 'var(--space-1)',
          background: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 60,
          minWidth: 160,
        }}>
          <button
            className="btn"
            onClick={() => { lock.lock('manual'); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: 0 }}
          >
            Lock Now
          </button>
          <button
            className="btn"
            onClick={() => { setChangingPassword(true); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: 0 }}
          >
            Change Password
          </button>
          <button
            className="btn"
            onClick={() => { handleRemovePassword(); setOpen(false); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: 0, color: 'var(--color-error)' }}
          >
            Remove Lock
          </button>
        </div>
      )}
    </div>
  );
}
